import { useState, useEffect } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ===== Templates Library Tab — Repeatable Systems =====
// 10 starter templates + ability to save your own. One-click to load into Design Studio.

const CATEGORY_META = {
  "Family": { color: "#CE93D8", icon: "fa-heart" },
  "Boutique": { color: "#FFB347", icon: "fa-gem" },
  "Master Spec": { color: "#00FFFF", icon: "fa-shield-halved" },
  "Coalition": { color: "#A78BFA", icon: "fa-dove" },
  "System": { color: "#00DC78", icon: "fa-puzzle-piece" },
  "Saved": { color: "#94A3B8", icon: "fa-bookmark" }
};

const TemplatesLibraryTab = () => {
  const [data, setData] = useState({ curated: [], saved: [] });
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/admin/design-templates`, { credentials: "include" });
        if (r.ok) {
          const json = await r.json();
          setData(json);
        }
      } catch {}
      setLoading(false);
    };
    fetchTemplates();
  }, []);

  const applyTemplate = (template) => {
    // Stash the template in localStorage — RoomVisualizer reads it on mount
    localStorage.setItem("mf_pending_template", JSON.stringify(template));
    // Safe in-app navigation (no page reload — Dashboard listens for this event)
    window.dispatchEvent(new CustomEvent("mf-switch-tab", { detail: { hub: "design-source", tab: "tools" } }));
  };

  // Crew Briefing PDF — modal-driven (no chained window.prompt)
  const [crewBriefOpen, setCrewBriefOpen] = useState(null);
  const [crewBriefForm, setCrewBriefForm] = useState({ unit_label: "", unit_address: "", crew_lead: "" });
  const [crewBriefLoading, setCrewBriefLoading] = useState(false);

  const closeCrewBriefModal = () => {
    setCrewBriefOpen(null);
    setCrewBriefForm({ unit_label: "", unit_address: "", crew_lead: "" });
    setCrewBriefLoading(false);
  };

  const submitCrewBrief = async () => {
    if (!crewBriefOpen || !crewBriefForm.unit_label.trim()) return;
    setCrewBriefLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/crew-briefing-pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          template_id: crewBriefOpen.template_id,
          unit_label: crewBriefForm.unit_label.trim(),
          unit_address: crewBriefForm.unit_address.trim(),
          crew_lead: crewBriefForm.crew_lead.trim(),
          materials_calc: {}
        })
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        alert("PDF failed: " + (err.detail || "unknown error"));
        setCrewBriefLoading(false);
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `crew-briefing-${crewBriefOpen.template_id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      closeCrewBriefModal();
    } catch (e) {
      alert("PDF failed: " + e.message);
      setCrewBriefLoading(false);
    }
  };

  // First Pitch Generator — opens modal-like flow
  const [pitchOpen, setPitchOpen] = useState(null);
  const [pitchForm, setPitchForm] = useState({ prospect_name: "", prospect_org: "", prospect_title: "", custom_angle: "" });
  const [pitchResult, setPitchResult] = useState(null);
  const [pitchLoading, setPitchLoading] = useState(false);

  const generateFirstPitch = async () => {
    if (!pitchOpen) return;
    setPitchLoading(true);
    setPitchResult(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/first-pitch-generator`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...pitchForm, template_id: pitchOpen.template_id })
      });
      if (!r.ok) {
        const err = await r.json();
        alert("Pitch failed: " + (err.detail || "unknown"));
        setPitchLoading(false);
        return;
      }
      const data = await r.json();
      setPitchResult(data);
    } catch (e) {
      alert("Pitch failed: " + e.message);
    }
    setPitchLoading(false);
  };

  const closePitchModal = () => {
    setPitchOpen(null);
    setPitchResult(null);
    setPitchForm({ prospect_name: "", prospect_org: "", prospect_title: "", custom_angle: "" });
  };

  const deleteSaved = async (templateId) => {
    if (!confirm("Delete this saved template?")) return;
    try {
      await fetch(`${BACKEND_URL}/api/admin/design-templates/${templateId}`, {
        method: "DELETE",
        credentials: "include"
      });
      setData(prev => ({ ...prev, saved: prev.saved.filter(s => s.template_id !== templateId) }));
    } catch {}
  };

  const allTemplates = [...data.curated, ...data.saved];
  const categories = ["All", ...new Set(allTemplates.map(t => t.category))];
  const filtered = activeCategory === "All" ? allTemplates : allTemplates.filter(t => t.category === activeCategory);

  if (loading) return <div style={{ padding: 40, color: "#7E92A8" }}>Loading templates…</div>;

  return (
    <div data-testid="templates-library-tab" style={{ padding: "20px 24px" }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ color: "#fff", margin: 0, fontSize: 24 }}>Repeatable Systems Library</h2>
        <p style={{ color: "#ADBED1", margin: "8px 0 0", fontSize: 14, lineHeight: 1.5 }}>
          One-click load into the Design Studio. Every template carries our doctrine: dignified palette,
          cycle-aligned scope, compliance-safe defaults, estimated material cost, and the B2B audiences it's perfect for.
        </p>
      </div>

      {/* Like I'm Five + Court-Ready explainer */}
      <details data-testid="templates-explainer" style={{ marginBottom: 22, background: "rgba(0,200,200,0.05)", border: "1px solid rgba(0,200,200,0.25)", borderRadius: 12, padding: "12px 16px" }}>
        <summary style={{ cursor: "pointer", color: "#00C8C8", fontSize: 13, fontWeight: 800, outline: "none", letterSpacing: "0.02em" }}>
          📖 Like I'm Five + Court-Ready — what is this page, in plain English?
        </summary>
        <div style={{ marginTop: 12, color: "#E6ECF2", fontSize: 13, lineHeight: 1.6 }}>
          <p style={{ margin: "0 0 10px" }}>
            <strong style={{ color: "#fff" }}>Plain English:</strong> Each card below is a "package" — like a meal at a restaurant. Pick the one that matches the room you're working on (a kid's room, a courthouse-grade rental, a senior apartment, etc.) and the system pre-fills the paint, flooring, fixtures, AND the cost estimate so you don't have to do the math.
          </p>
          <p style={{ margin: "0 0 10px" }}>
            <strong style={{ color: "#0d9488" }}>What the numbers mean:</strong> "$1,180–$1,650 materials" = what you'll pay your supplier (Sherwin Williams, Home Depot, etc.) before any markup. "22h labor" = how long the crew will be on-site, at $65/hr installer + $50/hr prep. Your <em>landlord proposal</em> adds 23% material curation + 18% margin on top — that's how you make profit.
          </p>
          <p style={{ margin: "0 0 4px", padding: "10px 12px", background: "rgba(255,179,71,0.08)", border: "1px solid rgba(255,179,71,0.25)", borderRadius: 8 }}>
            <strong style={{ color: "#FFB347" }}>👩‍⚖️ How to say this to a Court or Receiver:</strong>
            <br />
            <em style={{ color: "#E6ECF2" }}>&quot;Our firm operates on a templated, cycle-aligned scope system. Each unit type maps to a pre-vetted material variation with documented cost and labor bands. This eliminates change-order drift and gives the receiver a defensible per-unit budget before procurement begins.&quot;</em>
          </p>
        </div>
      </details>

      {/* Category filter pills */}
      <div style={{ display: "flex", gap: 8, marginBottom: 22, flexWrap: "wrap" }}>
        {categories.map(cat => {
          const meta = CATEGORY_META[cat] || { color: "#00FFFF", icon: "fa-folder" };
          const isActive = activeCategory === cat;
          return (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              data-testid={`templates-filter-${cat.replace(/\s+/g, '-').toLowerCase()}`}
              style={{
                padding: "8px 14px",
                borderRadius: 999,
                border: `1.5px solid ${isActive ? meta.color : "rgba(255,255,255,0.10)"}`,
                background: isActive ? `${meta.color}1a` : "transparent",
                color: isActive ? meta.color : "#ADBED1",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 6
              }}
            >
              {cat !== "All" && <i className={`fas ${meta.icon}`} style={{ fontSize: 10 }}></i>}
              {cat}
            </button>
          );
        })}
      </div>

      {/* Templates grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 }}>
        {filtered.map(t => {
          const meta = CATEGORY_META[t.category] || { color: "#00FFFF", icon: "fa-folder" };
          const isSaved = t.category === "Saved";
          return (
            <div
              key={t.template_id}
              data-testid={`template-card-${t.template_id}`}
              style={{
                background: "rgba(255,255,255,0.03)",
                border: `1px solid ${meta.color}33`,
                borderRadius: 12,
                padding: 16,
                display: "flex",
                flexDirection: "column",
                gap: 10,
                transition: "all 0.18s"
              }}
            >
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <i className={`fas ${t.icon || meta.icon}`} style={{ color: meta.color, fontSize: 16 }}></i>
                  <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 4, background: `${meta.color}22`, color: meta.color, fontWeight: 800, letterSpacing: "0.06em" }}>{t.category.toUpperCase()}</span>
                </div>
                {isSaved && (
                  <button onClick={() => deleteSaved(t.template_id)} title="Delete" style={{ background: "transparent", border: "none", color: "#7E92A8", cursor: "pointer", fontSize: 13 }}>✕</button>
                )}
              </div>
              <div>
                <strong style={{ color: "#fff", fontSize: 16, display: "block", marginBottom: 4 }}>{t.name}</strong>
                <p style={{ color: "#ADBED1", fontSize: 12, margin: 0, lineHeight: 1.4 }}>{t.subtitle}</p>
              </div>
              {t.tagline && (
                <p style={{ color: meta.color, fontSize: 12, margin: 0, fontStyle: "italic", lineHeight: 1.4 }}>"{t.tagline}"</p>
              )}
              {(t.estimated_cost_min || t.estimated_cost_max) ? (
                <div style={{ display: "flex", gap: 12, fontSize: 11, color: "#ADBED1", padding: "8px 0", borderTop: "1px solid rgba(255,255,255,0.06)", borderBottom: "1px solid rgba(255,255,255,0.06)" }} title="Materials = paint, flooring, fixtures, hardware (pre-tax, pre-markup). Labor = field hours at $65/hr installer + $50/hr prep, before cycle multipliers. This is the wholesale floor — your client invoice adds 23% material curation + 18% margin + cycle multiplier.">
                  <span><strong style={{ color: "#fff" }}>${t.estimated_cost_min}-${t.estimated_cost_max}</strong> materials</span>
                  {t.labor_hours ? <span>· <strong style={{ color: "#fff" }}>{t.labor_hours}h</strong> labor</span> : null}
                  <span style={{ marginLeft: "auto", fontSize: 10, color: "#7E92A8", fontStyle: "italic" }}>tap for what's included</span>
                </div>
              ) : null}
              {t.use_cases && t.use_cases.length > 0 && (
                <div>
                  <div style={{ fontSize: 10, color: "#7E92A8", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 4 }}>Best For</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {t.use_cases.slice(0, 3).map((uc, i) => (
                      <span key={i} style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: "rgba(255,255,255,0.05)", color: "#ADBED1" }}>{uc}</span>
                    ))}
                  </div>
                </div>
              )}
              <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
                <button
                  onClick={() => applyTemplate(t)}
                  data-testid={`template-use-${t.template_id}`}
                  style={{
                    padding: "10px 14px",
                    borderRadius: 8,
                    border: `1px solid ${meta.color}`,
                    background: `${meta.color}22`,
                    color: meta.color,
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer"
                  }}
                >
                  Use This Template →
                </button>
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    onClick={() => setCrewBriefOpen(t)}
                    data-testid={`template-crewbrief-${t.template_id}`}
                    title="Generate Crew Briefing PDF — print and slap on the truck dashboard"
                    style={{ flex: 1, padding: "7px 8px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.03)", color: "#ADBED1", fontSize: 10, fontWeight: 700, cursor: "pointer" }}
                  >
                    📋 Crew PDF
                  </button>
                  <button
                    onClick={() => { setPitchOpen(t); setPitchResult(null); }}
                    data-testid={`template-firstpitch-${t.template_id}`}
                    title="Generate First Pitch email for a prospect using this template"
                    style={{ flex: 1, padding: "7px 8px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.03)", color: "#ADBED1", fontSize: 10, fontWeight: 700, cursor: "pointer" }}
                  >
                    🎯 First Pitch
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div style={{ padding: 40, textAlign: "center", color: "#7E92A8" }}>
          No templates in this category yet. Generate a render in Design Studio and save it as a template.
        </div>
      )}

      {/* Crew Briefing PDF Modal — replaces window.prompt chain */}
      {crewBriefOpen && (
        <div data-testid="crew-brief-modal" style={{ position: "fixed", inset: 0, background: "rgba(3,11,26,0.85)", backdropFilter: "blur(10px)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: "#0A1A2E", border: "1px solid rgba(255,179,71,0.30)", borderRadius: 12, padding: 24, maxWidth: 520, width: "100%" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
              <div>
                <h3 style={{ color: "#fff", margin: "0 0 4px", fontSize: 18 }}>📋 Crew Briefing PDF</h3>
                <p style={{ color: "#ADBED1", margin: 0, fontSize: 12 }}>Template: <strong style={{ color: "#FFB347" }}>{crewBriefOpen.name}</strong></p>
              </div>
              <button onClick={closeCrewBriefModal} data-testid="crew-brief-close" style={{ background: "transparent", border: "none", color: "#7E92A8", fontSize: 20, cursor: "pointer" }}>✕</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
              <input
                type="text"
                placeholder="Unit label (e.g. 'Unit 312' or 'Cheyenne's bedroom') — required"
                value={crewBriefForm.unit_label}
                onChange={(e) => setCrewBriefForm(p => ({ ...p, unit_label: e.target.value }))}
                data-testid="crew-brief-unit-label"
                autoFocus
                style={{ padding: "10px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", color: "#fff", fontSize: 13 }}
              />
              <input
                type="text"
                placeholder="Unit address (optional)"
                value={crewBriefForm.unit_address}
                onChange={(e) => setCrewBriefForm(p => ({ ...p, unit_address: e.target.value }))}
                data-testid="crew-brief-unit-address"
                style={{ padding: "10px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", color: "#fff", fontSize: 13 }}
              />
              <input
                type="text"
                placeholder="Crew lead name (optional)"
                value={crewBriefForm.crew_lead}
                onChange={(e) => setCrewBriefForm(p => ({ ...p, crew_lead: e.target.value }))}
                data-testid="crew-brief-crew-lead"
                style={{ padding: "10px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", color: "#fff", fontSize: 13 }}
              />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={closeCrewBriefModal}
                data-testid="crew-brief-cancel"
                style={{ flex: 1, padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.10)", background: "transparent", color: "#ADBED1", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
              >
                Cancel
              </button>
              <button
                onClick={submitCrewBrief}
                disabled={crewBriefLoading || !crewBriefForm.unit_label.trim()}
                data-testid="crew-brief-generate"
                style={{ flex: 2, padding: "10px 14px", borderRadius: 8, border: "1px solid #FFB347", background: "rgba(255,179,71,0.15)", color: "#FFB347", fontSize: 13, fontWeight: 800, cursor: (crewBriefLoading || !crewBriefForm.unit_label.trim()) ? "not-allowed" : "pointer", opacity: (crewBriefLoading || !crewBriefForm.unit_label.trim()) ? 0.5 : 1 }}
              >
                {crewBriefLoading ? "Generating…" : "Generate PDF"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* First Pitch Modal */}
      {pitchOpen && (
        <div data-testid="first-pitch-modal" style={{ position: "fixed", inset: 0, background: "rgba(3,11,26,0.85)", backdropFilter: "blur(10px)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: "#0A1A2E", border: "1px solid rgba(0,255,255,0.25)", borderRadius: 12, padding: 24, maxWidth: 640, width: "100%", maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
              <div>
                <h3 style={{ color: "#fff", margin: "0 0 4px", fontSize: 18 }}>🎯 First Pitch Generator</h3>
                <p style={{ color: "#ADBED1", margin: 0, fontSize: 12 }}>Pairing template: <strong style={{ color: "#00FFFF" }}>{pitchOpen.name}</strong></p>
              </div>
              <button onClick={closePitchModal} style={{ background: "transparent", border: "none", color: "#7E92A8", fontSize: 20, cursor: "pointer" }}>✕</button>
            </div>

            {!pitchResult && (
              <>
                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
                  <input type="text" placeholder="Prospect name (e.g. Kenneth Duke)" value={pitchForm.prospect_name} onChange={(e) => setPitchForm(p => ({ ...p, prospect_name: e.target.value }))} data-testid="first-pitch-name" style={{ padding: "10px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", color: "#fff", fontSize: 13 }} />
                  <input type="text" placeholder="Organization (e.g. Dallas Housing Authority)" value={pitchForm.prospect_org} onChange={(e) => setPitchForm(p => ({ ...p, prospect_org: e.target.value }))} data-testid="first-pitch-org" style={{ padding: "10px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", color: "#fff", fontSize: 13 }} />
                  <input type="text" placeholder="Title (e.g. Director of Asset Management)" value={pitchForm.prospect_title} onChange={(e) => setPitchForm(p => ({ ...p, prospect_title: e.target.value }))} data-testid="first-pitch-title" style={{ padding: "10px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", color: "#fff", fontSize: 13 }} />
                  <textarea placeholder="Optional: founder-specific angle (e.g. 'I saw your post on RAD conversions...')" value={pitchForm.custom_angle} onChange={(e) => setPitchForm(p => ({ ...p, custom_angle: e.target.value }))} rows={2} data-testid="first-pitch-angle" style={{ padding: "10px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", color: "#fff", fontSize: 13, fontFamily: "inherit", resize: "vertical" }} />
                </div>
                <button onClick={generateFirstPitch} disabled={pitchLoading || !pitchForm.prospect_name} data-testid="first-pitch-generate" style={{ width: "100%", padding: "12px 20px", borderRadius: 8, border: "1px solid #00FFFF", background: "rgba(0,255,255,0.15)", color: "#00FFFF", fontSize: 14, fontWeight: 800, cursor: pitchLoading ? "wait" : "pointer" }}>
                  {pitchLoading ? "Generating pitch…" : "Generate First Pitch"}
                </button>
              </>
            )}

            {pitchResult && (
              <div data-testid="first-pitch-result" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ padding: 12, borderRadius: 8, background: "rgba(0,255,255,0.05)", border: "1px solid rgba(0,255,255,0.20)" }}>
                  <div style={{ fontSize: 10, color: "#00FFFF", letterSpacing: "0.08em", fontWeight: 800, marginBottom: 6 }}>SUBJECT LINE</div>
                  <strong style={{ color: "#fff", fontSize: 14 }}>{pitchResult.subject_line}</strong>
                </div>
                <div style={{ padding: 12, borderRadius: 8, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <span style={{ fontSize: 10, color: "#ADBED1", letterSpacing: "0.08em", fontWeight: 800 }}>EMAIL BODY</span>
                    <button onClick={() => navigator.clipboard.writeText(pitchResult.email_body)} style={{ padding: "4px 10px", borderRadius: 4, border: "1px solid rgba(0,255,255,0.3)", background: "transparent", color: "#00FFFF", fontSize: 10, cursor: "pointer" }}>Copy</button>
                  </div>
                  <p style={{ color: "#fff", fontSize: 13, lineHeight: 1.6, margin: 0, whiteSpace: "pre-wrap" }}>{pitchResult.email_body}</p>
                </div>
                <div style={{ padding: 12, borderRadius: 8, background: "rgba(167,139,250,0.04)", border: "1px solid rgba(167,139,250,0.20)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <span style={{ fontSize: 10, color: "#A78BFA", letterSpacing: "0.08em", fontWeight: 800 }}>LINKEDIN DM VARIANT</span>
                    <button onClick={() => navigator.clipboard.writeText(pitchResult.linkedin_dm_variant)} style={{ padding: "4px 10px", borderRadius: 4, border: "1px solid rgba(167,139,250,0.3)", background: "transparent", color: "#A78BFA", fontSize: 10, cursor: "pointer" }}>Copy</button>
                  </div>
                  <p style={{ color: "#fff", fontSize: 12, lineHeight: 1.5, margin: 0 }}>{pitchResult.linkedin_dm_variant}</p>
                </div>
                {pitchResult.follow_up_sequence && pitchResult.follow_up_sequence.length > 0 && (
                  <div style={{ padding: 12, borderRadius: 8, background: "rgba(255,179,71,0.04)", border: "1px solid rgba(255,179,71,0.20)" }}>
                    <div style={{ fontSize: 10, color: "#FFB347", letterSpacing: "0.08em", fontWeight: 800, marginBottom: 8 }}>FOLLOW-UP SEQUENCE (Day 3 + Day 7)</div>
                    {pitchResult.follow_up_sequence.map((fu, i) => (
                      <div key={i} style={{ marginBottom: 8, padding: 8, borderRadius: 4, background: "rgba(255,255,255,0.02)" }}>
                        <div style={{ fontSize: 10, color: "#FFB347", marginBottom: 4 }}>Day {i === 0 ? "3" : "7"}</div>
                        <p style={{ color: "#fff", fontSize: 12, lineHeight: 1.5, margin: 0 }}>{fu}</p>
                      </div>
                    ))}
                  </div>
                )}
                <button onClick={closePitchModal} style={{ padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.10)", background: "transparent", color: "#ADBED1", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Done</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ===== Palette Spotlight Tab — Weekly Auto-Post Generator =====
const PALETTE_OPTIONS_LIST = [
  { key: "ethereal_meadow", label: "Ethereal Meadow" },
  { key: "deep_harbor", label: "Deep Harbor" },
  { key: "earth_hearth", label: "Earth Hearth" },
  { key: "desert_twilight", label: "Desert Twilight" },
  { key: "cathedral_mauve", label: "Cathedral Mauve" },
  { key: "saffron_stone", label: "Saffron Stone" },
  { key: "midnight_sage", label: "Midnight Sage" },
  { key: "coastal_linen", label: "Coastal Linen" },
  { key: "heirloom_pearl", label: "Heirloom Pearl" },
  { key: "fairy_garden", label: "Fairy Garden" },
  { key: "modern_princess", label: "Modern Princess" },
  { key: "adventure_cove", label: "Adventure Cove" },
  { key: "dreamy_pastels", label: "Dreamy Pastels" },
  { key: "modern_neutral", label: "Modern Neutral" },
  { key: "warm_contemporary", label: "Warm Contemporary" },
  { key: "cool_modern", label: "Cool Modern" },
  { key: "classic_clean", label: "Classic Clean" },
];

const TONE_OPTIONS = [
  { key: "thought_leader", label: "Thought Leader (institutional)" },
  { key: "warm_relational", label: "Warm Relational (community)" },
  { key: "direct_sales", label: "Direct Sales (CTA-driven)" },
  { key: "grandma_love", label: "Grandma Love (personal)" }
];

const PaletteSpotlightTab = () => {
  const [paletteKey, setPaletteKey] = useState(PALETTE_OPTIONS_LIST[Math.floor((new Date()).getDate() / 2) % PALETTE_OPTIONS_LIST.length].key);
  const [tone, setTone] = useState("thought_leader");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  const generate = async () => {
    setGenerating(true);
    setError(null);
    setResult(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/palette-spotlight`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ palette_key: paletteKey, tone })
      });
      if (!r.ok) {
        const err = await r.json();
        throw new Error(err.detail || "Generation failed");
      }
      const data = await r.json();
      setResult(data);
    } catch (e) {
      setError(e.message);
    }
    setGenerating(false);
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 1500);
  };

  return (
    <div data-testid="palette-spotlight-tab" style={{ padding: "20px 24px" }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ color: "#fff", margin: 0, fontSize: 24 }}>
          <i className="fas fa-bullhorn" style={{ marginRight: 10, color: "#FFB347" }}></i>
          Palette Spotlight — Weekly Post Generator
        </h2>
        <p style={{ color: "#ADBED1", margin: "8px 0 0", fontSize: 14, lineHeight: 1.5 }}>
          One palette per week. AI generates a ready-to-post Instagram + LinkedIn caption with hashtags, hooks, CTAs, AND a B2B variant for asset managers. You approve, you post. Stealth thought leadership for dignified turnover.
        </p>
      </div>

      <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,179,71,0.20)", borderRadius: 12, padding: 18, marginBottom: 18 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
          <div>
            <label style={{ display: "block", fontSize: 12, color: "#ADBED1", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 6 }}>This Week&apos;s Variation</label>
            <select
              value={paletteKey}
              onChange={(e) => setPaletteKey(e.target.value)}
              data-testid="spotlight-palette-select"
              style={{ width: "100%", padding: "11px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", color: "#fff", fontSize: 14 }}
            >
              {PALETTE_OPTIONS_LIST.map(p => (
                <option key={p.key} value={p.key} style={{ background: "#030B1A" }}>{p.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, color: "#ADBED1", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, marginBottom: 6 }}>Tone</label>
            <select
              value={tone}
              onChange={(e) => setTone(e.target.value)}
              data-testid="spotlight-tone-select"
              style={{ width: "100%", padding: "11px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", color: "#fff", fontSize: 14 }}
            >
              {TONE_OPTIONS.map(t => (
                <option key={t.key} value={t.key} style={{ background: "#030B1A" }}>{t.label}</option>
              ))}
            </select>
          </div>
        </div>
        <button
          onClick={generate}
          disabled={generating}
          data-testid="spotlight-generate-btn"
          style={{
            width: "100%",
            padding: "13px 20px",
            borderRadius: 8,
            border: "1px solid #FFB347",
            background: "linear-gradient(135deg, rgba(255,179,71,0.20), rgba(255,179,71,0.08))",
            color: "#FFB347",
            fontSize: 14,
            fontWeight: 800,
            cursor: generating ? "wait" : "pointer",
            letterSpacing: "0.02em"
          }}
        >
          {generating ? "Generating spotlight post…" : "✨ Generate This Week's Post"}
        </button>
      </div>

      {error && (
        <div data-testid="spotlight-error" style={{ padding: 14, borderRadius: 8, background: "rgba(255,82,82,0.08)", border: "1px solid rgba(255,82,82,0.3)", color: "#FF5252", marginBottom: 18 }}>
          {error}
        </div>
      )}

      {result && (
        <div data-testid="spotlight-result" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Ready-to-post block */}
          <div style={{ background: "rgba(255,179,71,0.06)", border: "1px solid rgba(255,179,71,0.25)", borderRadius: 12, padding: 18 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <strong style={{ color: "#FFB347", fontSize: 13, letterSpacing: "0.08em", textTransform: "uppercase" }}>📱 Instagram / Facebook Post</strong>
              <button
                onClick={() => copyToClipboard(result.ready_to_post, "main")}
                data-testid="spotlight-copy-main"
                style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid rgba(255,179,71,0.4)", background: "transparent", color: "#FFB347", fontSize: 11, fontWeight: 700, cursor: "pointer" }}
              >
                {copiedField === "main" ? "✓ Copied" : "Copy Post"}
              </button>
            </div>
            <pre style={{ color: "#fff", fontSize: 14, lineHeight: 1.6, whiteSpace: "pre-wrap", margin: 0, fontFamily: "inherit" }}>{result.ready_to_post}</pre>
          </div>

          {/* B2B LinkedIn variant */}
          <div style={{ background: "rgba(0,255,255,0.04)", border: "1px solid rgba(0,255,255,0.20)", borderRadius: 12, padding: 18 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <strong style={{ color: "#00FFFF", fontSize: 13, letterSpacing: "0.08em", textTransform: "uppercase" }}>💼 LinkedIn B2B Variant</strong>
              <button
                onClick={() => copyToClipboard(result.b2b_pitch_variant, "b2b")}
                data-testid="spotlight-copy-b2b"
                style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid rgba(0,255,255,0.4)", background: "transparent", color: "#00FFFF", fontSize: 11, fontWeight: 700, cursor: "pointer" }}
              >
                {copiedField === "b2b" ? "✓ Copied" : "Copy LinkedIn"}
              </button>
            </div>
            <p style={{ color: "#fff", fontSize: 14, lineHeight: 1.6, margin: 0 }}>{result.b2b_pitch_variant}</p>
          </div>

          {/* Image Prompt */}
          <div style={{ background: "rgba(206,147,216,0.04)", border: "1px solid rgba(206,147,216,0.20)", borderRadius: 12, padding: 18 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <strong style={{ color: "#CE93D8", fontSize: 13, letterSpacing: "0.08em", textTransform: "uppercase" }}>🎨 Image Prompt</strong>
              <button
                onClick={() => copyToClipboard(result.image_prompt, "img")}
                data-testid="spotlight-copy-img"
                style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid rgba(206,147,216,0.4)", background: "transparent", color: "#CE93D8", fontSize: 11, fontWeight: 700, cursor: "pointer" }}
              >
                {copiedField === "img" ? "✓ Copied" : "Copy Prompt"}
              </button>
            </div>
            <p style={{ color: "#ADBED1", fontSize: 13, lineHeight: 1.5, margin: "0 0 10px" }}>Use this in Design Studio's room visualizer OR any image generator (DALL-E, Midjourney) to create the accompanying graphic:</p>
            <p style={{ color: "#fff", fontSize: 13, lineHeight: 1.6, margin: 0, fontStyle: "italic" }}>"{result.image_prompt}"</p>
          </div>

          <div style={{ display: "flex", gap: 10, color: "#7E92A8", fontSize: 11 }}>
            <span>Generated {new Date(result.generated_at).toLocaleString()}</span>
            <span>· Vibe: {result.vibe}</span>
            <span>· Tone: {result.tone}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export { TemplatesLibraryTab, PaletteSpotlightTab };
